/**
 * The Problems strip (PRP-WEB-2 W1.5): what in the plan needs a look, room by room.
 *
 * - overlap: two rooms on one level share area deeper than `problem_overlap_min_in`
 *   (`overlapDepth`, overlap.ts); `overlapIn` is the depth, the distance that nudges them apart.
 * - touches_nothing: a room shares no wall with any other room (`sharedMap`), on a level
 *   with more than one room. A room already reported as overlapping is not repeated here.
 * - no_door: a room with no door and no missing wall. A room in a subroom group is
 *   reached through its group: it is not reported when another room of the group has one.
 * - estimated: a room with a measurement marked `estimated: true` (written by chat,
 *   PRP-MCP-17; read-only here).
 *
 * Order: overlaps, then rooms touching nothing, then no door, then estimated; each in plan order.
 */
import { POLICY, IN_PER_FT } from './policy';
import { overlapDepth } from './overlap';
import { sharedMap } from './shared';
import { groupOf, type Groups } from './groups';
import type { Measurement, Plan, Room } from './types';

export type ProblemKind = 'overlap' | 'touches_nothing' | 'no_door' | 'estimated';

export interface PlanProblem {
  kind: ProblemKind;
  /** Room ids involved (two for an overlap, one otherwise). */
  rooms: string[];
  /** Overlap only: how far the two rooms overlap, inches (the distance that would nudge them apart). */
  overlapIn?: number;
  /** One plain sentence naming the room(s). */
  message: string;
}

const HUNDREDTHS = 100; // policy-literal-ok: display rounding of inches, not a threshold

/** 3 in; 2 ft 4 in. */
function inchesText(inches: number): string {
  const whole = Math.round(inches);
  const ft = Math.floor(whole / IN_PER_FT), rest = whole % IN_PER_FT;
  if (!ft) return `${Math.max(1, rest)} in`;
  return rest ? `${ft} ft ${rest} in` : `${ft} ft`;
}

const isEstimated = (m: Measurement): boolean => (m as Measurement & { estimated?: unknown }).estimated === true;
const hasWay = (r: Room): boolean => r.openings.some((o) => o.kind === 'door' || o.kind === 'missing');

export function planProblems(plan: Plan, groups: Groups = []): PlanProblem[] {
  const rooms = plan.rooms;
  const overlaps: PlanProblem[] = [], lonely: PlanProblem[] = [], doorless: PlanProblem[] = [], estimated: PlanProblem[] = [];
  const overlapping = new Set<string>();
  for (let i = 0; i < rooms.length; i++) {
    for (let j = i + 1; j < rooms.length; j++) {
      const A = rooms[i], B = rooms[j];
      if (A.level !== B.level) continue;
      const inches = overlapDepth(A.corners, B.corners) * IN_PER_FT;
      if (inches <= POLICY.problem_overlap_min_in) continue;
      const overlapIn = Math.round(inches * HUNDREDTHS) / HUNDREDTHS;
      overlapping.add(A.id).add(B.id);
      overlaps.push({ kind: 'overlap', rooms: [A.id, B.id], overlapIn, message: `${A.name} and ${B.name} overlap by ${inchesText(inches)}` });
    }
  }
  const M = sharedMap(plan);
  const perLevel = new Map<string, number>();
  rooms.forEach((r) => perLevel.set(r.level, (perLevel.get(r.level) ?? 0) + 1));
  for (const r of rooms) {
    const touches = [...(M.get(r.id)?.values() ?? [])].some((ivs) => ivs.length > 0);
    if (!touches && !overlapping.has(r.id) && (perLevel.get(r.level) ?? 0) > 1) {
      lonely.push({ kind: 'touches_nothing', rooms: [r.id], message: `${r.name} touches no other room` });
    }
    if (!hasWay(r)) {
      const g = groupOf(groups, r.id)?.group;
      const viaGroup = !!g && [g.main_room_id, ...g.subroom_ids].some((id) => {
        const q = id !== r.id ? rooms.find((x) => x.id === id) : undefined;
        return !!q && hasWay(q);
      });
      if (!viaGroup) doorless.push({ kind: 'no_door', rooms: [r.id], message: `${r.name} has no door` });
    }
    if (r.measurements.some(isEstimated) || (r.spans ?? []).some(isEstimated)) {
      estimated.push({ kind: 'estimated', rooms: [r.id], message: `${r.name} has an estimated size` });
    }
  }
  return [...overlaps, ...lonely, ...doorless, ...estimated];
}
